import { useMemo } from 'react';

import { useAuthenticatedAxios } from './axiosInstance';

// Hook for admin API operations
export const useAdminApi = () => {
    const api = useAuthenticatedAxios();

    return useMemo(
        () => ({
            // Get admin dashboard statistics
            fetchStats: async () => {
                const response = await api.get('/api/admin/stats');
                return response.data;
            },

            // Get pending ownership claims
            fetchPendingClaims: async (page = 1, limit = 20) => {
                const response = await api.get('/api/admin/claims', {
                    params: { page, limit },
                });
                return response.data;
            },

            // Review ownership claim (approve/reject)
            reviewClaim: async (orgId, claimId, status, adminNotes = '') => {
                const response = await api.put(
                    `/api/admin/claims/${orgId}/${claimId}`,
                    { status, adminNotes }
                );
                return response.data;
            },

            // Get pending organizations
            fetchPendingOrganizations: async (page = 1, limit = 20) => {
                const response = await api.get(
                    '/api/admin/organizations/pending',
                    {
                        params: { page, limit },
                    }
                );
                return response.data;
            },

            // Get unowned organizations
            fetchUnownedOrganizations: async (page = 1, limit = 20) => {
                const response = await api.get(
                    '/api/admin/organizations/unowned',
                    {
                        params: { page, limit },
                    }
                );
                return response.data;
            },

            // Assign owner to organization
            assignOwner: async (orgId, userId, role = 'owner') => {
                const response = await api.post(
                    `/api/admin/organizations/${orgId}/assign-owner`,
                    { userId, role }
                );
                return response.data;
            },

            // Update organization status (approve/reject)
            updateOrganizationStatus: async (
                orgId,
                status,
                adminNotes = ''
            ) => {
                const response = await api.put(
                    `/api/organizations/${orgId}/status`,
                    { status, adminNotes }
                );
                return response.data;
            },

            // Search users by name or email
            searchUsers: async (query, limit = 10) => {
                const response = await api.get('/api/users/search', {
                    params: { q: query, limit },
                });
                return response.data;
            },

            // Assign member to organization (alias for assignOwner)
            assignMember: async (orgId, userId, role = 'owner') => {
                const response = await api.post(
                    `/api/admin/organizations/${orgId}/assign-owner`,
                    { userId, role }
                );
                return response.data;
            },

            // Get dead links
            fetchDeadLinks: async () => {
                const response = await api.get('/api/admin/url-checks');
                return response.data;
            },
        }),
        [api]
    );
};

export default useAdminApi;
